import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Button, Typography, IconButton, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Grid, Alert, Snackbar,
  Skeleton, InputAdornment, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Tooltip, Stack
} from '@mui/material';
import { Add, Edit, Delete, Search, Business, Clear, Close, Computer } from '@mui/icons-material';
import { createClient, updateClient, deleteClient } from '../api/services/userService/Client';
import NodeAssignmentDialog from './NodeAssignmentDialog';

export default function ClientManagement({ userPermissions, sharedData, onRefresh }) {
  const { keycloak } = useKeycloak();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNodeDialog, setOpenNodeDialog] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    name: '', tenantId: '', abbr: '', title: '', description: '',
    logoUrl: '', faviconUrl: '',
    theme: { primary_color: '#50C878', secondary_color: '#8b5cf6', background_color: '#ffffff' },
    font: { family: '' }
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const canManage = useCallback(() => {
    if (!userPermissions) return false;
    return userPermissions.isSuperAdmin || Object.values(userPermissions.clientRoles || {}).includes('admin');
  }, [userPermissions]);

  const filterByPermissions = useCallback((list) => {
    if (!userPermissions || userPermissions.isSuperAdmin) return list;
    return list.filter(c => userPermissions.clientIds?.includes(c.id));
  }, [userPermissions]);

  const filteredClients = useMemo(() => {
    let result = filterByPermissions(sharedData?.clients || []);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(term) ||
        c.abbr?.toLowerCase().includes(term) ||
        c.tenantId?.toLowerCase().includes(term)
      );
    }
    return result;
  }, [sharedData?.clients, searchTerm, filterByPermissions]);

  const paginatedClients = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredClients.slice(start, start + rowsPerPage);
  }, [filteredClients, page, rowsPerPage]);

  useEffect(() => {
    setClients(paginatedClients);
  }, [paginatedClients]);

  const openCreateDialog = () => {
    setIsEdit(false);
    setFormData({
      name: '', tenantId: '', abbr: '', title: '', description: '',
      logoUrl: '', faviconUrl: '',
      theme: { primary_color: '#50C878', secondary_color: '#8b5cf6', background_color: '#ffffff' },
      font: { family: '' }
    });
    setOpenDialog(true);
  };

  const openEditDialog = (client) => {
    setIsEdit(true);
    setCurrentClient(client);
    setFormData({
      name: client.name || '',
      tenantId: client.tenantId || '',
      abbr: client.abbr || '',
      title: client.title || '',
      description: client.description || '',
      logoUrl: client.logoUrl || '',
      faviconUrl: client.faviconUrl || '',
      theme: client.theme || { primary_color: '#50C878', secondary_color: '#8b5cf6', background_color: '#ffffff' },
      font: client.font || { family: '' }
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setNotification({ open: true, message: 'Name required', severity: 'error' });
      return;
    }

    try {
      await keycloak.updateToken(30);

      const data = {
        name: formData.name,
        tenantId: formData.tenantId,
        abbreviation: formData.abbr,
        title: formData.title,
        description: formData.description,
        logoUrl: formData.logoUrl,
        faviconUrl: formData.faviconUrl,
        theme: formData.theme,
        font: formData.font
      };

      const res = isEdit
        ? await updateClient({ id: currentClient.id, ...data }, keycloak.token)
        : await createClient(data, keycloak.token);

      if (res.success) {
        setNotification({ open: true, message: `Client ${isEdit ? 'updated' : 'created'}`, severity: 'success' });
        if (onRefresh) await onRefresh();
        setOpenDialog(false);
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await keycloak.updateToken(30);
      const res = await deleteClient(currentClient.id, keycloak.token);
      if (res.success) {
        setNotification({ open: true, message: 'Deleted', severity: 'success' });
        if (onRefresh) await onRefresh();
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
      setOpenDeleteDialog(false);
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>Clients</Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {canManage() && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} size="small">
            Add Client
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
              <TableCell>Name</TableCell>
              <TableCell>Tenant ID</TableCell>
              <TableCell>Abbreviation</TableCell>
              <TableCell>Theme</TableCell>
              {canManage() && <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  {canManage() && <TableCell><Skeleton width={60} /></TableCell>}
                </TableRow>
              ))
            ) : clients.length > 0 ? (
              clients.map(client => (
                <TableRow key={client.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: 1,
                          bgcolor: client.theme?.primary_color || 'primary.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontSize: '0.875rem', fontWeight: 600
                        }}
                      >
                        {client.name?.[0]?.toUpperCase() || 'C'}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{client.name}</Typography>
                        {client.title && (
                          <Typography variant="caption" color="text.secondary">{client.title}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {client.tenantId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {client.abbr ? (
                      <Chip label={client.abbr} size="small" variant="outlined" sx={{ height: 22 }} />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {client.theme ? (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Primary">
                          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: client.theme.primary_color, border: '1px solid', borderColor: 'divider' }} />
                        </Tooltip>
                        <Tooltip title="Secondary">
                          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: client.theme.secondary_color, border: '1px solid', borderColor: 'divider' }} />
                        </Tooltip>
                      </Stack>
                    ) : '—'}
                  </TableCell>
                  {canManage() && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentClient(client);
                          setOpenNodeDialog(true);
                        }}
                        title="Manage Nodes"
                      >
                        <Computer fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditDialog(client)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => { setCurrentClient(client); setOpenDeleteDialog(true); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canManage() ? 5 : 4} sx={{ py: 8, textAlign: 'center' }}>
                  <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No clients found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredClients.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit' : 'New'} Client</Typography>
            <IconButton onClick={() => setOpenDialog(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                fullWidth size="small" label="Name" required
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small" label="Abbreviation"
                value={formData.abbr}
                onChange={e => setFormData(p => ({ ...p, abbr: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Tenant ID" disabled={isEdit}
                value={formData.tenantId}
                onChange={e => setFormData(p => ({ ...p, tenantId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Title"
                value={formData.title}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Description" multiline rows={2}
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label="Logo URL"
                value={formData.logoUrl}
                onChange={e => setFormData(p => ({ ...p, logoUrl: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label="Favicon URL"
                value={formData.faviconUrl}
                onChange={e => setFormData(p => ({ ...p, faviconUrl: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Theme Colors</Typography>
              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography variant="caption">Primary</Typography>
                  <input
                    type="color"
                    value={formData.theme?.primary_color || '#50C878'}
                    onChange={e => setFormData(p => ({ ...p, theme: { ...p.theme, primary_color: e.target.value } }))}
                    style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption">Secondary</Typography>
                  <input
                    type="color"
                    value={formData.theme?.secondary_color || '#8b5cf6'}
                    onChange={e => setFormData(p => ({ ...p, theme: { ...p.theme, secondary_color: e.target.value } }))}
                    style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption">Background</Typography>
                  <input
                    type="color"
                    value={formData.theme?.background_color || '#ffffff'}
                    onChange={e => setFormData(p => ({ ...p, theme: { ...p.theme, background_color: e.target.value } }))}
                    style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Font Family"
                placeholder="e.g., Inter, Roboto"
                value={formData.font?.family || ''}
                onChange={e => setFormData(p => ({ ...p, font: { family: e.target.value } }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>{isEdit ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs">
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{currentClient?.name}</strong>? This affects all departments and teams.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <NodeAssignmentDialog
        open={openNodeDialog}
        onClose={() => setOpenNodeDialog(false)}
        entityType="client"
        entityId={currentClient?.id}
        entityName={currentClient?.name}
        onSuccess={onRefresh}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification(p => ({ ...p, open: false }))} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
